import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  CASE_KEY,
  casePagePreviewUrl,
  casePageUrl,
  conflictKind,
  extractionPageUrl,
  extractionResult,
  formChoiceOf,
  NO_EXTRACTION,
  ProblemError,
  requestOf,
  retryExtraction,
  reviewOf,
  startExtraction,
  useCase,
  useExtraction,
  type CustomerOut,
  type DocumentOut,
  type ExtractionOut,
  type PageChoice,
} from "@/api";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { PageTitle } from "@/components/PageTitle";
import { StateMessage } from "@/components/StateMessage";
import {
  OTHER_PAGE_PARAM,
  originalOf,
  otherPageHref,
  otherPageOutcome,
  readFromLabel,
} from "@/lib/otherPage";
import { choiceOf, needsPageChoice, pageTiles, sameTile, tileName } from "@/lib/pageChoice";
import { useDocumentHidden } from "@/lib/useDocumentHidden";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useDemoRun } from "@/store/demoRun";

import styles from "./ReadingJobScreen.module.css";
import { OtherPageChoice, type OtherPageResult } from "./OtherPageChoice";
import { PagePicker } from "./PagePicker";
import { ReadingSheet, type SheetSource } from "./ReadingSheet";
import { ReadingSkeleton } from "./ReadingSkeleton";
import { READING_DURATION, ReadingSteps } from "./ReadingSteps";

/**
 * The one sentence a person gets for a failure here (PF-5). `ProblemError`'s
 * own `.message` is the problem's **title** — a German status word, not the
 * server's actual refusal (the retry's own 409, for one) — so `.userMessage`
 * is what has to be shown; same pattern as `components/DropArea.tsx` and
 * `features/pricing/ApprovalPanel.tsx`.
 */
function messageOf(cause: unknown): string {
  return cause instanceof ProblemError
    ? cause.userMessage
    : cause instanceof Error
      ? cause.message
      : String(cause);
}

/**
 * From two minutes on a reading takes longer than usual — twice the upper end
 * of `READING_DURATION` (Welle 1, idea 24). Plan 4 §2 said three minutes
 * against „etwa 1–2 Minuten"; kept there, the usual sentence would have stood
 * under a clock past 2:00.
 */
export const LONGER_THAN_USUAL_S = 120;

/**
 * After „Diese Lesart nehmen — Maße lesen“ (plan 4 §9): the outline is the
 * person's, only the wall question runs — about half a full reading
 * (12–20 s in the browser test of 11.10.2026).
 */
export const CHOSEN_FORM_TEXT =
  "Umriss von dir gewählt — jetzt werden seine Maße gelesen. Dauert meist 15–30 Sekunden; du kannst die Seite verlassen.";

/**
 * What the tab says when the reading ended while it was out of sight
 * (Kauf-Test 11.10.2026): a person who went to another tab while it read
 * sees from there that it is done, and comes back to the review.
 */
export const READ_IN_BACKGROUND = "✓ Zeichnung gelesen";
export const FAILED_IN_BACKGROUND = "Lesen fehlgeschlagen";

/** The files the reading takes — the same three types the drop on the start page accepts. */
const READABLE = new Set(["application/pdf", "image/jpeg", "image/png"]);

export function readableDrawings(documents: readonly DocumentOut[] | undefined): DocumentOut[] {
  return (documents ?? [])
    .filter((document) => READABLE.has(document.mimeType))
    .sort((a, b) => a.sortIndex - b.sortIndex);
}

/** `42` → „0:42“, `191` → „3:11“ — a clock, not a measurement. */
export function clockText(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/**
 * Seconds since the job started (or was queued), ticking once a second while
 * it runs. Read from the server's timestamps, so a reload keeps counting from
 * where the job really is — not from when this page was opened.
 */
function useRunningFor(job: ExtractionOut | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());
  const running = job?.status === "running" || job?.status === "queued";
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  const since = job ? Date.parse(job.startedAt ?? job.createdAt) : Number.NaN;
  return Number.isNaN(since) ? null : Math.max(0, (now - since) / 1000);
}

/**
 * „Zeichnung lesen“ — one reading of one Vorgang, while the worker has it
 * (design 2026-09-23 §3.6, plan 4 §2 step 2).
 *
 * The URL is the state: a reload asks the server what the job is doing and
 * shows it — nothing here starts a job on its own. Three steps in words, the
 * running one with its mesh strip, a clock that says how long it has been —
 * no ring and no percentage (CI §6): nobody knows how far along a reading is,
 * only that it is under way. Beside them the drawing itself. Succeeded → the
 * review. Failed → the reason and the two ways on: read again, or draw the
 * outline by hand.
 *
 * A Vorgang without a reading (Kauf-Test 11.10.2026) is not a failure: with
 * drawings on file the screen offers to read them — one press, the person's,
 * never on opening —, without any it says where a drawing goes and that the
 * outline can be drawn instead.
 *
 * In a hidden tab the reading is still watched (`inBackground`), so the
 * tab's title can say it is done. The move on to the review waits until the
 * tab is seen again: made in the background, the review's own title would
 * have replaced the one that said so.
 */
export function ReadingJobScreen() {
  const { caseId = "" } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [params] = useSearchParams();
  const adoptExtraction = useDemoRun((state) => state.adoptExtraction);
  const job = useExtraction(caseId || null, { inBackground: true });
  const detail = useCase(caseId || null);
  const hidden = useDocumentHidden();
  const outcome = job.data?.status;
  useDocumentTitle(
    hidden && outcome === "succeeded"
      ? READ_IN_BACKGROUND
      : hidden && outcome === "failed"
        ? FAILED_IN_BACKGROUND
        : "Zeichnung lesen",
  );
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  /**
   * The page this screen started a reading of (Seitenwahl). Until the worker
   * has turned it, the sheet beside the steps is this page — not page 1 of
   * the document, which in the case that started this was a photo.
   */
  const [readPage, setReadPage] = useState<PageChoice | null>(null);
  /** What came of „Diese Seite lesen“ on „Andere Seite wählen“, short of a new reading. */
  const [otherResult, setOtherResult] = useState<OtherPageResult | null>(null);

  const source = job.data?.source ?? null;
  const original = source !== null ? originalOf(source, detail.data?.documents) : null;
  // With a Stand, a reading could never become Stand 1: nothing here reads
  // again — the server refuses too (409 stand_exists) —, the way is the board.
  const planningHref = (detail.data?.latestVersionNo ?? 0) > 0 ? `/vorgang/${caseId}/planung` : null;
  // „Andere Seite wählen“ (Seitenwahl, Folgepunkt a): offered after a failed
  // or unsure reading of a page sheet whose original is on file, and never
  // where a Stand exists. `?seiten=1` opens the choice; asked for anywhere
  // else, the parameter is ignored and the screen is what it would be.
  const otherOutcome = planningHref === null && original !== null ? otherPageOutcome(job.data) : null;
  const choosingOther = otherOutcome !== null && params.get(OTHER_PAGE_PARAM) === "1";

  // Read → the review. A v2 reading whose form is not sure carries a review
  // and no schema view (`_view_of_v2`): it moves on all the same — waiting
  // for a view it will never have kept „Form nicht sicher“ on this screen.
  // Only a v1 reading goes into the demo store (review I-1): a v2 `result`
  // there would let the old schema view take READ walls over without the
  // re-check; the review reads its reading from the server.
  useEffect(() => {
    if (job.data?.status !== "succeeded" || hidden || choosingOther) return;
    const result = extractionResult(job.data);
    const v2 = reviewOf(job.data) !== null;
    if (result === null && !v2) return;
    if (result !== null && !v2) adoptExtraction(caseId, result);
    navigate(`/vorgang/${caseId}/schema`, { replace: true });
  }, [job.data, hidden, choosingOther, caseId, adoptExtraction, navigate]);

  const drawings = readableDrawings(detail.data?.documents);
  // The page a job reads is the server's to say (`source`): its first
  // document, page 1 — on a page sheet the sheet itself, stored before the
  // job was queued. Before any job, the chosen page if this screen knows it;
  // where more than one page is on file and none is known, nothing rather
  // than a page that may be the wrong one. The page the worker turned takes
  // over once it has the job.
  const documentPage =
    readPage !== null
      ? casePageUrl(caseId, readPage.documentId, readPage.page)
      : source !== null
        ? casePageUrl(caseId, source.documentId, source.page)
        : drawings[0] && !needsPageChoice(drawings)
          ? casePageUrl(caseId, drawings[0].id, 1)
          : null;
  // What the sheet's head says: „Seite 2 aus „Angebot.pdf““ for a page
  // sheet — after a reload too —, the chosen page while the server has not
  // answered yet, „Seite 1“ where only one page is on file.
  const readNumber = readPage?.page ?? source?.originalPage ?? null;
  const readName =
    source !== null && source.originalPage !== null
      ? readFromLabel(source, original)
      : readNumber !== null
        ? `Seite ${readNumber}`
        : null;
  const drawHref = drawHrefFor(caseId);
  // After the measures of a chosen reading failed: back to the readings of
  // the form, as the server names them on the failed job (review I-1).
  const otherReadingHref =
    job.data?.status === "failed" && formChoiceOf(job.data) !== null ? `/vorgang/${caseId}/schema?lesarten=1` : null;

  async function retry() {
    setRetrying(true);
    setRetryError(null);
    try {
      await retryExtraction(caseId);
      await job.refetch();
    } catch (cause) {
      setRetryError(messageOf(cause));
    } finally {
      setRetrying(false);
    }
  }

  async function start(choice: PageChoice | null) {
    setStarting(true);
    setStartError(null);
    try {
      if (choice !== null) {
        // Exactly the chosen document, and its page — the server refuses
        // anything else with a sentence of its own (422).
        await startExtraction(caseId, [choice.documentId], choice);
        setReadPage(choice);
      } else {
        await startExtraction(
          caseId,
          drawings.map((drawing) => drawing.id),
        );
      }
      // The answer to this query is a 404 until now; asked again it is the
      // job just queued, and the screen turns into its progress in place.
      await job.refetch();
    } catch (cause) {
      setStartError(messageOf(cause));
    } finally {
      setStarting(false);
    }
  }

  /**
   * „Diese Seite lesen“ on „Andere Seite wählen“: the reading's own start,
   * on the original (`source.originalDocumentId`) with the page chosen.
   * Another page, or the same after a failure → a new reading, and the
   * screen turns into its progress. The same page read before → that
   * reading stands (200, `succeeded`) and is said so, with the way to it.
   */
  async function startOther(choice: PageChoice) {
    setStarting(true);
    setOtherResult(null);
    try {
      const started = await startExtraction(caseId, [choice.documentId], choice);
      if (started.status === "succeeded") {
        // Current again on the server: the review route shows it.
        await job.refetch();
        setOtherResult({ kind: "same", page: choice.page });
        return;
      }
      setReadPage(choice);
      // Asked again first: the newest job is then the one just queued, and
      // leaving `?seiten=1` cannot fall back on the old answer — which, for
      // an unsure reading, would bounce straight to the review.
      await job.refetch();
      navigate(`/vorgang/${caseId}/einlesen`, { replace: true });
    } catch (cause) {
      if (cause instanceof ProblemError && cause.status === 409 && conflictKind(cause.problem.raw) === "stand_exists") {
        setOtherResult({ kind: "stand" });
        void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
      } else {
        setOtherResult({ kind: "error", detail: messageOf(cause) });
      }
    } finally {
      setStarting(false);
    }
  }

  function showPartial() {
    const result = job.data ? extractionResult(job.data) : null;
    // A v2 job that stopped has no partial view (H3); only v1 gets here.
    if (result === null || (job.data && reviewOf(job.data) !== null)) return;
    adoptExtraction(caseId, result);
    navigate(`/vorgang/${caseId}/schema`);
  }

  // In the page's own geometry, under the frame that already names the
  // Vorgang — never a loading line (Task 10, B-4).
  if (job.isPending) return <ReadingSkeleton />;
  if (job.isError && job.error.message === NO_EXTRACTION) {
    if (detail.isPending) return <ReadingSkeleton />;
    if (detail.isError) {
      return (
        <StateMessage
          tone="conflict"
          title="Der Vorgang lässt sich nicht laden."
          action={
            <Link className="inline-link" to={drawHref}>
              Selbst zeichnen
            </Link>
          }
        >
          {messageOf(detail.error)}
        </StateMessage>
      );
    }
    return (
      <ReadingStart
        drawings={drawings}
        sheet={{ reading: null, document: documentPage }}
        recordHref={`/vorgang/${caseId}`}
        drawHref={drawHref}
        planningHref={planningHref}
        pagePreviewUrl={(documentId, page) => casePagePreviewUrl(caseId, documentId, page)}
        starting={starting}
        startError={startError}
        onChoiceChange={() => setStartError(null)}
        onStart={(choice) => void start(choice)}
      />
    );
  }
  if (choosingOther && original !== null && source !== null) {
    return (
      <OtherPageChoice
        original={original}
        readPage={source.originalPage}
        outcome={otherOutcome}
        pagePreviewUrl={(documentId, page) => casePagePreviewUrl(caseId, documentId, page)}
        backHref={otherOutcome === "unsure" ? `/vorgang/${caseId}/schema` : `/vorgang/${caseId}/einlesen`}
        drawHref={drawHref}
        planningHref={`/vorgang/${caseId}/planung`}
        starting={starting}
        result={otherResult}
        resultHref={`/vorgang/${caseId}/schema`}
        onChoiceChange={() => setOtherResult(null)}
        onStart={(choice) => void startOther(choice)}
      />
    );
  }
  if (job.isError) {
    return (
      <StateMessage
        tone="conflict"
        title="Die Lesung lässt sich nicht anzeigen."
        action={
          <Link className="inline-link" to={drawHref}>
            Selbst zeichnen
          </Link>
        }
      >
        {messageOf(job.error)}
      </StateMessage>
    );
  }

  return (
    <ReadingProgress
      job={job.data}
      sheet={{
        // The worker turns and stores the page once it has the job; before
        // that the route has nothing to show.
        reading: job.data.status === "queued" ? null : extractionPageUrl(caseId, 1),
        document: documentPage,
      }}
      pageLabel={readNumber !== null ? `Seite ${readNumber}` : needsPageChoice(drawings) ? null : "Seite 1"}
      readName={readName}
      drawHref={drawHref}
      planningHref={planningHref}
      otherReadingHref={otherReadingHref}
      otherPageHref={otherOutcome === "failed" ? otherPageHref(caseId) : null}
      retrying={retrying}
      retryError={retryError}
      onRetry={() => void retry()}
      onPartial={showPartial}
      // Not before the Vorgang has answered: „noch nicht zugeordnet“ would
      // stand for a moment over a customer who is there.
      customerLine={
        detail.data !== undefined ? (
          <CustomerLine
            caseId={caseId}
            customer={detail.data.customer ?? null}
            cancelled={detail.data.status === "cancelled"}
          />
        ) : null
      }
    />
  );
}

/** The drawing becomes this Vorgang's Stand 1 (UX gate round 1, R-4) — the free `/umriss` would open a second Vorgang. */
function drawHrefFor(caseId: string): string {
  return `/umriss?case=${encodeURIComponent(caseId)}`;
}

/**
 * A Vorgang that has no reading yet (Kauf-Test 11.10.2026) — the view of
 * `ReadingJobScreen`'s 404, apart so the dev preview can show it.
 *
 * With drawings on file: the sheet, the three steps as they are about to
 * run, „Noch nicht gelesen.“ and the one button that starts them. Without
 * any: where a drawing goes — the record, whose column takes a dropped file
 * — and the outline by hand for this Vorgang.
 *
 * With more than one page on file (Seitenwahl, 13.10.2026) the sheet becomes
 * every page as a small picture, and the button reads the one a person
 * chose — see `PageChoiceStart`.
 */
export function ReadingStart({
  drawings,
  sheet,
  recordHref,
  drawHref,
  planningHref = null,
  pagePreviewUrl,
  starting,
  startError,
  onChoiceChange,
  onStart,
}: {
  drawings: readonly DocumentOut[];
  sheet: SheetSource;
  recordHref: string;
  drawHref: string;
  /**
   * Set where the Vorgang has its Stand already: a reading could never
   * become Stand 1 there, so the drawing is not sent to be read — the way on
   * is the board.
   */
  planningHref?: string | null;
  /** The small drawing of one stored page, for the choice of the page. */
  pagePreviewUrl: (documentId: string, page: number) => string;
  starting: boolean;
  startError: string | null;
  /** Another page chosen: a refusal of the previous one no longer applies. */
  onChoiceChange?: () => void;
  /** `null`: read the drawings as they are — one page on file, nothing to choose. */
  onStart: (choice: PageChoice | null) => void;
}) {
  if (planningHref !== null) {
    return (
      <section className={`${styles.screen} ${styles.empty}`}>
        <PageTitle className={styles.title}>Zeichnung lesen</PageTitle>
        {/* A locked screen: the centred card with the mesh sign (Task 11,
            UX-Gutachten 1, B-15). */}
        <EmptyState
          title="Für diesen Vorgang gibt es schon einen Stand."
          actions={
            <Link className={styles.wayLink} to={planningHref}>
              Zur Planung
            </Link>
          }
        >
          Eine Lesung würde ihn nicht ersetzen — die Zeichnung wird deshalb nicht gelesen.
        </EmptyState>
      </section>
    );
  }
  if (drawings.length === 0) {
    return (
      <section className={`${styles.screen} ${styles.empty}`}>
        <PageTitle className={styles.title}>Zeichnung lesen</PageTitle>
        {/* An empty screen: the centred card with the mesh sign (Task 11,
            UX-Gutachten 1, B-15). */}
        <EmptyState
          title="Für diesen Vorgang liegt keine Zeichnung vor."
          actions={
            <div className={styles.ways}>
              <Link className={styles.wayLink} to={recordHref}>
                Zur Akte
              </Link>
              <Link className={`inline-link ${styles.quietWay}`} to={drawHref}>
                Umriss selbst zeichnen
              </Link>
            </div>
          }
        >
          Leg die Zeichnung in der Akte ab oder zeichne den Umriss selbst.
        </EmptyState>
      </section>
    );
  }
  if (needsPageChoice(drawings)) {
    return (
      <PageChoiceStart
        drawings={drawings}
        drawHref={drawHref}
        pagePreviewUrl={pagePreviewUrl}
        starting={starting}
        startError={startError}
        onChoiceChange={onChoiceChange}
        onStart={onStart}
      />
    );
  }

  return (
    <section className={`${styles.screen} ${styles.withSheet}`}>
      <PageTitle className={styles.title}>Zeichnung lesen</PageTitle>
      <div className={styles.layout}>
        <div className={styles.side}>
          <ReadingSteps stage={null} status={null} />
          <div className={styles.start}>
            <p className={styles.startLead}>Noch nicht gelesen.</p>
            <p className={styles.startText}>
              Gelesen werden Umriss und Maße, danach prüfst du den Vorschlag. {READING_DURATION}
            </p>
            <div className={styles.ways}>
              {/* The screen's one main action, so K60 red (design 2026-10-24 §8.2). */}
              <Button variant="primary" busy={starting} onClick={() => onStart(null)}>
                {starting ? "Startet …" : "Zeichnung lesen"}
              </Button>
              <Link className={`inline-link ${styles.quietWay}`} to={drawHref}>
                Umriss selbst zeichnen
              </Link>
            </div>
          </div>
          {startError ? (
            <StateMessage tone="conflict" live="alert" title="Nicht gestartet.">
              {startError}
            </StateMessage>
          ) : null}
        </div>
        <ReadingSheet source={sheet} note={drawings[0]!.originalFilename} missing="Vorschau nicht geladen." />
      </div>
    </section>
  );
}

/**
 * „Welche Seite zeigt den Umriss?“ (Seitenwahl, 13.10.2026). The reading
 * reads one page; with more than one on file a person says which — in one
 * document or across several, one raster grouped by file. Nothing is chosen
 * on opening (product rule „prüft, bestätigt nicht“), the button says why it
 * waits, and a refusal of the server stands under the button that drew it.
 *
 * The raster comes first in the reading order and the button right after
 * it: narrow, the pages stand above the action that reads one; wide, the
 * raster takes the sheet's place and the action stands beside it.
 */
function PageChoiceStart({
  drawings,
  drawHref,
  pagePreviewUrl,
  starting,
  startError,
  onChoiceChange,
  onStart,
}: {
  drawings: readonly DocumentOut[];
  drawHref: string;
  pagePreviewUrl: (documentId: string, page: number) => string;
  starting: boolean;
  startError: string | null;
  onChoiceChange?: () => void;
  onStart: (choice: PageChoice | null) => void;
}) {
  const [chosen, setChosen] = useState<PageChoice | null>(null);
  const readRef = useRef<HTMLButtonElement>(null);
  const ids = useId();
  const titleId = `${ids}-title`;
  const statusId = `${ids}-status`;
  const errorId = `${ids}-error`;
  const tiles = pageTiles(drawings);
  const several = drawings.length > 1;
  const chosenTile = tiles.find((tile) => sameTile(chosen, tile)) ?? null;

  return (
    <section className={`${styles.screen} ${styles.withSheet}`}>
      <PageTitle className={styles.title}>Zeichnung lesen</PageTitle>
      <div className={`${styles.layout} ${styles.choosing}`}>
        <section className={styles.pages} aria-labelledby={titleId}>
          <div className={styles.pagesHead}>
            <h2 id={titleId} className={styles.pagesTitle}>
              Welche Seite zeigt den Umriss?
            </h2>
            <p className={styles.pagesHint}>
              {several ? (
                <>
                  <span className="tabular">{drawings.length}</span> Unterlagen, zusammen{" "}
                  <span className="tabular">{tiles.length}</span> Seiten.
                </>
              ) : (
                <>
                  {drawings[0]!.originalFilename} hat <span className="tabular">{tiles.length}</span> Seiten.
                </>
              )}{" "}
              Gelesen wird nur die, die du wählst.
            </p>
          </div>
          <PagePicker
            drawings={drawings}
            previewUrl={pagePreviewUrl}
            chosen={chosen}
            labelledBy={titleId}
            onChoose={(tile) => {
              if (!sameTile(chosen, tile)) onChoiceChange?.();
              setChosen(choiceOf(tile));
            }}
            onCommit={() => readRef.current?.focus()}
          />
        </section>
        <div className={styles.side}>
          <div className={styles.start}>
            <p className={styles.startLead}>Noch nicht gelesen.</p>
            <p className={styles.startText}>
              Gelesen werden Umriss und Maße, danach prüfst du den Vorschlag. {READING_DURATION}
            </p>
            {/* Live: a choice made with the arrows is said, and the button
                is described by it — what „Diese Seite lesen“ will read. */}
            <p id={statusId} role="status" className={styles.chosenLine}>
              {chosenTile !== null ? `${tileName(chosenTile, several)} gewählt.` : "Wähl zuerst eine Seite."}
            </p>
            <div className={styles.ways}>
              {/* The screen's one main action, so K60 red (design 2026-10-24 §8.2). */}
              <Button
                ref={readRef}
                variant="primary"
                busy={starting}
                aria-disabled={chosen === null ? "true" : undefined}
                aria-describedby={startError ? `${statusId} ${errorId}` : statusId}
                data-role="read-page"
                onClick={() => {
                  if (chosen !== null) onStart(chosen);
                }}
              >
                {starting ? "Startet …" : "Diese Seite lesen"}
              </Button>
              <Link className={`inline-link ${styles.quietWay}`} to={drawHref}>
                Umriss selbst zeichnen
              </Link>
            </div>
            {startError ? (
              <StateMessage id={errorId} tone="conflict" live="alert" title="Nicht gestartet.">
                {startError}
              </StateMessage>
            ) : null}
          </div>
          <ReadingSteps stage={null} status={null} />
        </div>
      </div>
    </section>
  );
}

/**
 * Who the customer is, in one slim line under the title (spec 2026-10-20
 * §3.2): while the reading runs in the worker is the natural time to name
 * them. Without a customer it says so and leads to the record's customer
 * dialog (`?kunde=1`, the same arrival the register row's „kein Kunde“ and
 * the print preview use) — the reading goes on in the worker while a person
 * is away. With one it names them; nothing here assigns anybody.
 *
 * A cancelled Vorgang takes no customer (the server answers 409): the line
 * says how it stands and offers no way to change it. Not a live region — the
 * status sentence beside the steps is the screen's one.
 */
export function CustomerLine({
  caseId,
  customer,
  cancelled = false,
}: {
  caseId: string;
  customer: Pick<CustomerOut, "company" | "displayName"> | null;
  cancelled?: boolean;
}) {
  if (customer !== null) {
    return (
      <p className={styles.customerLine}>
        Kunde: <span className={styles.customerName}>{customer.company ?? customer.displayName}</span>
      </p>
    );
  }
  return (
    <p className={styles.customerLine}>
      Kunde: noch nicht zugeordnet
      {cancelled ? null : (
        <>
          {" — "}
          <Link className="inline-link" to={`/vorgang/${encodeURIComponent(caseId)}?kunde=1`}>
            zuordnen
          </Link>
        </>
      )}
    </p>
  );
}

/**
 * What a person sees of one job — the view of `ReadingJobScreen`, apart so
 * the dev preview can show every state without a worker (plan 4 §2).
 */
export function ReadingProgress({
  job: data,
  sheet,
  pageLabel = "Seite 1",
  readName = null,
  drawHref,
  planningHref = null,
  otherReadingHref = null,
  otherPageHref = null,
  retrying,
  retryError,
  onRetry,
  onPartial,
  customerLine = null,
}: {
  job: ExtractionOut;
  sheet: SheetSource;
  /**
   * Which page is read, for the sheet's caption — the chosen one after a
   * Seitenwahl; `null` where this screen does not know (a reload with more
   * than one page on file), and then the caption names none.
   */
  pageLabel?: string | null;
  /**
   * The page read, with the document it came from — „Seite 2 aus
   * „Angebot.pdf““ for a page sheet (`source`, also after a reload). Named
   * on the sheet's head and on a failure; `pageLabel` where it is `null`.
   */
  readName?: string | null;
  drawHref: string;
  /** Set where the Vorgang has its Stand: a failure is not read again (review I-2). */
  planningHref?: string | null;
  /** „Andere Lesart“ after the measures of a chosen reading failed (review I-1). */
  otherReadingHref?: string | null;
  /** „Andere Seite wählen“ after a reading of a page sheet failed (Seitenwahl, Folgepunkt a). */
  otherPageHref?: string | null;
  retrying: boolean;
  retryError: string | null;
  onRetry: () => void;
  onPartial: () => void;
  /** Who the customer is (`CustomerLine`), shown while the reading runs or waits; not on a failure. */
  customerLine?: ReactNode;
}) {
  const runningFor = useRunningFor(data);
  const drawInstead = (
    <Link className="inline-link" to={drawHref}>
      Selbst zeichnen
    </Link>
  );

  if (data.status === "failed" && planningHref !== null) {
    return (
      <section className={styles.screen}>
        <PageTitle className={styles.title}>Zeichnung lesen</PageTitle>
        <StateMessage
          tone="conflict"
          title="Lesen fehlgeschlagen."
          action={
            <Link className={styles.wayLink} to={planningHref}>
              Zur Planung
            </Link>
          }
        >
          Für diesen Vorgang gibt es schon einen Stand — eine neue Lesung würde ihn nicht ersetzen.
        </StateMessage>
      </section>
    );
  }

  if (data.status === "failed") {
    return (
      <section className={styles.screen}>
        <PageTitle className={styles.title}>Zeichnung lesen</PageTitle>
        <StateMessage tone="conflict" title="Lesen fehlgeschlagen.">
          {otherPageHref !== null
            ? "Lies noch einmal, wähl eine andere Seite oder zeichne den Umriss selbst."
            : "Lies noch einmal oder zeichne den Umriss selbst."}
          {readName !== null ? <span className={styles.detail}>Gelesen: {readName}.</span> : null}
          {data.message ? <span className={styles.detail}>{data.message}</span> : null}
        </StateMessage>
        <div className={styles.ways}>
          <Button variant="tinte" busy={retrying} onClick={onRetry}>
            {retrying ? "Liest noch einmal …" : "Noch einmal lesen"}
          </Button>
          {otherPageHref !== null ? (
            <Link className={styles.wayLink} to={otherPageHref}>
              Andere Seite wählen
            </Link>
          ) : null}
          {otherReadingHref !== null ? (
            <Link className={styles.wayLink} to={otherReadingHref}>
              Andere Lesart
            </Link>
          ) : null}
          <Link className={styles.wayLink} to={drawHref}>
            Selbst zeichnen
          </Link>
          {data.partial && data.result ? (
            <Button variant="leise" onClick={onPartial}>
              Teilergebnis ansehen
            </Button>
          ) : null}
        </div>
        {retryError ? (
          <StateMessage tone="conflict" title="Nicht neu gestartet.">
            {retryError}
          </StateMessage>
        ) : null}
      </section>
    );
  }

  const queued = data.status === "queued";
  const longer = runningFor !== null && runningFor >= LONGER_THAN_USUAL_S;
  const request = requestOf(data);
  const chosenForm = request?.kind === "choose_form";
  return (
    <section className={`${styles.screen} ${styles.withSheet}`} aria-busy={!data.stalled}>
      <PageTitle className={styles.title}>Zeichnung lesen</PageTitle>
      {customerLine}
      <div className={styles.layout}>
        <div className={styles.side}>
          <ReadingSteps stage={data.stage ?? null} status={data.status} request={request} />
          <div className={styles.status}>
            {/* A sentence, not the clock, is the live part: a region that
                changed every second would talk over everything else. */}
            <p role="status" className={styles.statusText}>
              {queued
                ? "Wartet auf einen freien Platz — startet gleich."
                : longer
                  ? "Dauert länger als üblich. Du kannst später wiederkommen."
                  : chosenForm
                    ? CHOSEN_FORM_TEXT
                    : `${READING_DURATION} Du kannst die Seite verlassen — gelesen wird weiter.`}
            </p>
            {runningFor !== null && !queued ? (
              <p className={styles.clock}>
                läuft seit <span className="tabular">{clockText(runningFor)}</span>
              </p>
            ) : null}
          </div>
          {data.stalled ? (
            queued ? (
              <StateMessage title="Wartet seit fünf Minuten auf einen freien Platz." action={drawInstead}>
                Du kannst weiter warten oder den Umriss selbst zeichnen.
              </StateMessage>
            ) : (
              <StateMessage tone="conflict" title="Seit fünf Minuten keine Rückmeldung." action={drawInstead}>
                Du kannst warten oder den Umriss selbst zeichnen.
              </StateMessage>
            )
          ) : null}
        </div>
        <ReadingSheet
          source={sheet}
          note={readName ?? pageLabel ?? ""}
          state={queued ? "wartet" : "wird gelesen"}
          alt={pageLabel !== null ? `${pageLabel} deiner Zeichnung` : "Die Seite, die gelesen wird"}
          missing="Vorschau nicht geladen — gelesen wird trotzdem."
        />
      </div>
    </section>
  );
}
